import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AlertCircle, TrendingUp, Radio, ArrowRight, ExternalLink, GitBranch } from "lucide-react";
import { radar } from "@/data/fixtures";
import SourceChip from "@/components/SourceChip";
import ApprovalModal from "@/components/ApprovalModal";
import useHashHighlight from "@/hooks/useHashHighlight";

const severityStyle = {
  High: { color: "var(--mos-amber)", bg: "var(--mos-amber-bg)", border: "var(--mos-amber-border)" },
  Medium: { color: "#B45309", bg: "#FEF3C7", border: "#FDE68A" },
  Low: { color: "#065F46", bg: "#D1FAE5", border: "#A7F3D0" },
};

export default function CommitmentRisk() {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [ackTarget, setAckTarget] = useState(null);
  useHashHighlight();

  // Cross-module link: r1 (Northlake SO 30117) ↔ Suppliers s2 (Apex ISO 13485 cert expired).
  const crossLinks = {
    r1: {
      to: "/suppliers#s2",
      label: "See supplier cert lapse driving this",
      testId: "xlink-r1-to-suppliers",
    },
  };

  const onAcknowledge = (item) => setAckTarget(item);
  const onApproveAck = () => {
    toast.success(`Risk acknowledged · logged for ${ackTarget.salesOrder} · pending R. Diaz review`, { duration: 4000 });
    setAckTarget(null);
  };

  return (
    <div data-testid="page-commitment-risk" className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2 flex items-center gap-2">
            <Radio size={12} />
            Commitment Risk Radar
          </div>
          <h1 className="text-[30px] leading-tight font-semibold text-slate-900 tracking-tight">
            {radar.summary.atRisk} at-risk of {radar.summary.total} open commitments
          </h1>
          <p className="mt-2 text-[13.5px] text-slate-600">
            Combined exposure{" "}
            <span className="mos-mono text-slate-900 font-medium">
              {radar.summary.exposureTotal}
            </span>
            {" "}· {radar.summary.netChange}. Last recomputed{" "}
            <span className="mos-mono">{radar.generatedAt}</span>.
          </p>
        </div>
      </div>

      {/* Ranked list */}
      <div className="space-y-4">
        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold flex items-center gap-3">
          <span>Ranked by exposure</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>

        {radar.items.map((r) => {
          const s = severityStyle[r.riskLabel] || severityStyle.Medium;
          const isOpen = expandedId === r.id;
          return (
            <div
              key={r.id}
              className="mos-surface rounded-md overflow-hidden"
              data-testid={`radar-item-${r.id}`}
            >
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <span
                    className="mt-0.5 h-8 w-8 flex-none inline-flex items-center justify-center rounded-full"
                    style={{ background: s.bg }}
                  >
                    <AlertCircle size={14} style={{ color: s.color }} />
                  </span>

                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-[16px] font-semibold text-slate-900">
                        {r.customer}
                      </span>
                      <span className="mos-mono text-[12px] px-2 py-0.5 rounded border"
                        style={{ background: "#F1F5F9", borderColor: "var(--mos-border)", color: "var(--mos-text)" }}
                      >
                        {r.salesOrder}
                      </span>
                      <span
                        className="text-[10.5px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded border"
                        style={{ color: s.color, background: s.bg, borderColor: s.border }}
                        data-testid={`radar-severity-${r.id}`}
                      >
                        {r.riskLabel} · score {r.riskScore}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-4 gap-x-6 gap-y-2 text-[13px]">
                      <Field label="Order value" value={r.orderValue} mono />
                      <Field
                        label={r.exposureLabel}
                        value={r.exposure}
                        mono={r.exposure.startsWith("$")}
                        tone={r.exposure.startsWith("$") ? "amber" : "muted"}
                      />
                      <Field label="Committed" value={r.committedDelivery} mono />
                      <Field
                        label="Decision runway"
                        value={<span><span className="mos-mono">{r.runwayDays}</span> days</span>}
                      />
                    </div>

                    <div className="mt-4 flex items-center gap-3 text-[13px]">
                      <span className="text-slate-500">Recommended action</span>
                      <span className="text-slate-900 font-medium">{r.recommendedAction}</span>
                    </div>

                    {crossLinks[r.id] && (
                      <button
                        type="button"
                        onClick={() => navigate(crossLinks[r.id].to)}
                        className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-[color:var(--mos-accent)] hover:underline font-medium"
                        data-testid={crossLinks[r.id].testId}
                      >
                        <GitBranch size={12} />
                        {crossLinks[r.id].label} →
                      </button>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {r.linkToReasoning ? (
                      <button
                        type="button"
                        onClick={() => navigate("/reasoning")}
                        className="mos-btn mos-btn-primary"
                        data-testid={`btn-view-reasoning-${r.id}`}
                      >
                        View reasoning
                        <ArrowRight size={14} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                        className="mos-btn mos-btn-secondary"
                        data-testid={`btn-decompose-${r.id}`}
                      >
                        {isOpen ? "Hide" : "Decompose"} score
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onAcknowledge(r)}
                      className="mos-btn mos-btn-ghost text-[12px]"
                      data-testid={`btn-acknowledge-${r.id}`}
                    >
                      Acknowledge
                    </button>
                  </div>
                </div>
              </div>

              {isOpen && (
                <div
                  className="border-t px-5 py-4"
                  style={{ borderColor: "var(--mos-border)", background: "var(--mos-surface-2)" }}
                  data-testid={`radar-decomposition-${r.id}`}
                >
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                    <TrendingUp size={12} />
                    Score decomposition · SP-02
                  </div>
                  <div className="space-y-2.5">
                    {r.signals.map((sig) => (
                      <div key={sig.label} className="flex items-center gap-4">
                        <div className="w-14 mos-mono text-[13px] text-slate-900">
                          {Math.round(sig.weight * 100)}%
                        </div>
                        <div
                          className="h-1.5 rounded-full overflow-hidden flex-none"
                          style={{ width: "160px", background: "#E5E7EB" }}
                        >
                          <div
                            style={{
                              width: `${sig.weight * 100}%`,
                              background: "var(--mos-accent)",
                              height: "100%",
                            }}
                          />
                        </div>
                        <div className="flex-1 text-[13px] text-slate-700">
                          {sig.label}
                        </div>
                        <SourceChip
                          system={sig.source.split(" · ")[0]}
                          record={sig.source.split(" · ").slice(1).join(" · ")}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11.5px] text-slate-500 flex items-center gap-2">
        <ExternalLink size={11} />
        Every risk item is a recommendation. External customer notifications and PO changes require a named approval per SP-01.
      </p>

      {/* Acknowledge modal (reused approval gate pattern) */}
      <ApprovalModal
        open={!!ackTarget}
        onCancel={() => setAckTarget(null)}
        onApprove={onApproveAck}
        title="Acknowledge risk item"
        rationale="Acknowledgement is a logged, attributed action. It does not change source systems and does not notify the customer. It marks the item as reviewed by a named person."
        approver="J. Okafor"
        action={ackTarget ? `Acknowledge risk on ${ackTarget.salesOrder}` : ""}
        systemsAffected={["Radar registry"]}
        auditNote="Acknowledgement will appear in the audit log."
        confirmLabel="Acknowledge and log"
      />
    </div>
  );
}

function Field({ label, value, mono, tone }) {
  const color =
    tone === "amber"
      ? "var(--mos-amber)"
      : tone === "muted"
        ? "var(--mos-text-subtle)"
        : "var(--mos-text)";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">
        {label}
      </div>
      <div
        className={"text-[13.5px] font-medium " + (mono ? "mos-mono" : "")}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
